import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
  Image,
  Modal,
  FlatList } from 'react-native'
import {
  ArrowLeft,
  ChevronRight,
  Check,
  X,
  FileText,
  Coins,
  Package,
  ClipboardList,
  type LucideIcon } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { getProductSuggestions } from '../lib/openrouter'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import { useToast } from '../components/Toast'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantProductEdit'>
  route: RouteProp<RootStackParamList, 'MerchantProductEdit'>
}

interface Category {
  id: string
  name_fi: string
  name_en: string | null
  parent_id: string | null
  icon: string | null
}

interface Supplier {
  id: string
  name: string
}

interface ProductImage {
  id: string
  image_url: string
}

interface ProductData {
  title_fi: string
  title_en: string
  description_fi: string
  description_en: string
  price: string
  cost_price: string
  sku: string
  barcode: string
  stock_quantity: string
  low_stock_threshold: string
  reorder_quantity: string
  supplier_id: string | null
  supplier_product_code: string
  category_id: string | null
  ownership_type: 'store_owned' | 'consignment' | 'seller'
  condition_grade: string
  condition_notes: string
  target_margin_percent: string
  minimum_price: string
}

const CONDITION_GRADES = [
  { value: 'mint', label: 'Mint', labelFi: 'Uudenveroinen' },
  { value: 'excellent', label: 'Excellent', labelFi: 'Erinomainen' },
  { value: 'good', label: 'Good', labelFi: 'Hyv\u00e4' },
  { value: 'fair', label: 'Fair', labelFi: 'Kohtalainen' },
  { value: 'poor', label: 'Poor', labelFi: 'Heikko' },
  { value: 'as_is', label: 'As-Is', labelFi: 'Sellaisenaan' },
]

export default function MerchantProductEditScreen({ navigation, route }: Props) {
  const { storeId, productId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const toast = useToast()
  const isNew = !productId

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [images, setImages] = useState<ProductImage[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [showCategoryPicker, setShowCategoryPicker] = useState(false)
  const [showSupplierPicker, setShowSupplierPicker] = useState(false)
  const [showConditionPicker, setShowConditionPicker] = useState(false)
  const [activeSection, setActiveSection] = useState<'basic' | 'pricing' | 'inventory' | 'details'>('basic')

  const [data, setData] = useState<ProductData>({
    title_fi: '',
    title_en: '',
    description_fi: '',
    description_en: '',
    price: '',
    cost_price: '',
    sku: '',
    barcode: '',
    stock_quantity: '1',
    low_stock_threshold: '0',
    reorder_quantity: '',
    supplier_id: null,
    supplier_product_code: '',
    category_id: null,
    ownership_type: 'store_owned',
    condition_grade: '',
    condition_notes: '',
    target_margin_percent: '',
    minimum_price: '' })

  useEffect(() => {
    fetchCategories()
    fetchSuppliers()
    if (productId) {
      fetchProduct()
    }
  }, [productId, storeId])

  const fetchCategories = async () => {
    const { data: cats } = await supabase
      .from('categories')
      .select('id, name_fi, name_en, parent_id, icon')
      .eq('is_active', true)
      .order('sort_order')

    setCategories(cats || [])
  }

  const fetchSuppliers = async () => {
    const { data: sups } = await supabase
      .from('suppliers')
      .select('id, name')
      .eq('flea_market_id', storeId)
      .eq('is_active', true)
      .order('name')

    setSuppliers(sups || [])
  }

  const fetchProduct = async () => {
    try {
      const { data: product, error } = await supabase
        .from('products')
        .select(`
          *,
          product_images (id, image_url)
        `)
        .eq('id', productId)
        .single()

      if (error) throw error

      setData({
        title_fi: product.title_fi || product.title || '',
        title_en: product.title_en || '',
        description_fi: product.description_fi || product.description || '',
        description_en: product.description_en || '',
        price: product.price?.toString() || '',
        cost_price: product.cost_price?.toString() || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        stock_quantity: product.stock_quantity?.toString() || '1',
        low_stock_threshold: product.low_stock_threshold?.toString() || '0',
        reorder_quantity: product.reorder_quantity?.toString() || '',
        supplier_id: product.supplier_id || null,
        supplier_product_code: product.supplier_product_code || '',
        category_id: product.category_id || null,
        ownership_type: product.ownership_type || 'store_owned',
        condition_grade: product.condition_grade || '',
        condition_notes: product.condition_notes || '',
        target_margin_percent: product.target_margin_percent?.toString() || '',
        minimum_price: product.minimum_price?.toString() || '' })
      setImages(product.product_images || [])
    } catch (error) {
      console.error('Error fetching product:', error)
      toast.error(t('error') || 'Virhe', 'Failed to load product')
      navigation.goBack()
    } finally {
      setLoading(false)
    }
  }

  const getAISuggestion = async () => {
    const imageUrl = images[0]?.image_url
    if (!imageUrl) {
      toast.error(t('error') || 'Virhe', t('no_image_for_ai') || 'No image available for AI analysis')
      return
    }

    setSuggesting(true)
    try {
      const suggestion = await getProductSuggestions(imageUrl)
      setData(prev => ({
        ...prev,
        title_fi: suggestion.title_fi || prev.title_fi,
        title_en: suggestion.title_en || prev.title_en,
        description_fi: suggestion.description_fi || prev.description_fi,
        description_en: suggestion.description_en || prev.description_en,
        price: suggestion.price?.toFixed(2) || prev.price }))
    } catch (error: any) {
      console.error('AI suggestion error:', error)
      toast.error(t('error') || 'Virhe', 'Failed to get AI suggestions')
    } finally {
      setSuggesting(false)
    }
  }

  const calculateMargin = () => {
    const price = parseFloat(data.price)
    const cost = parseFloat(data.cost_price)
    if (price > 0 && cost > 0) {
      return (((price - cost) / price) * 100).toFixed(1)
    }
    return null
  }

  const calculateSuggestedPrice = () => {
    const cost = parseFloat(data.cost_price)
    const margin = parseFloat(data.target_margin_percent)
    if (cost > 0 && margin > 0) {
      return (cost / (1 - margin / 100)).toFixed(2)
    }
    return null
  }

  const saveProduct = async (publish: boolean = false) => {
    if (!data.title_fi && !data.title_en) {
      toast.error(t('error') || 'Virhe', t('title_required') || 'Please enter a title')
      return
    }

    if (!data.price || isNaN(parseFloat(data.price))) {
      toast.error(t('error') || 'Virhe', t('valid_price_required') || 'Please enter a valid price')
      return
    }

    setSaving(true)
    try {
      const barcode = data.barcode || `P${Date.now().toString(36).toUpperCase()}`

      const productData = {
        title: data.title_fi || data.title_en,
        title_fi: data.title_fi || null,
        title_en: data.title_en || null,
        description: data.description_fi || data.description_en || null,
        description_fi: data.description_fi || null,
        description_en: data.description_en || null,
        price: parseFloat(data.price),
        cost_price: data.cost_price ? parseFloat(data.cost_price) : null,
        sku: data.sku || null,
        barcode,
        stock_quantity: parseInt(data.stock_quantity) || 1,
        low_stock_threshold: parseInt(data.low_stock_threshold) || 0,
        reorder_quantity: data.reorder_quantity ? parseInt(data.reorder_quantity) : null,
        supplier_id: data.supplier_id || null,
        supplier_product_code: data.supplier_product_code || null,
        category_id: data.category_id || null,
        ownership_type: data.ownership_type,
        condition_grade: data.condition_grade || null,
        condition_notes: data.condition_notes || null,
        target_margin_percent: data.target_margin_percent ? parseFloat(data.target_margin_percent) : null,
        minimum_price: data.minimum_price ? parseFloat(data.minimum_price) : null,
        status: publish ? 'for_sale' : 'draft',
        flea_market_id: storeId,
        seller_id: user?.id }

      if (isNew) {
        const { error } = await supabase
          .from('products')
          .insert(productData)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', productId)

        if (error) throw error
      }

      const msg = publish
        ? (t('product_published') || 'Product published!')
        : (t('draft_saved') || 'Draft saved!')
      toast.success(t('success') || 'Valmis', msg); setTimeout(() => navigation.goBack(), 1000)
    } catch (error: any) {
      console.error('Save error:', error)
      toast.error(t('error') || 'Virhe', error.message)
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof ProductData, value: string | null) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  const getCategoryName = (id: string | null) => {
    if (!id) return t('select_category') || 'Select category'
    const cat = categories.find(c => c.id === id)
    if (!cat) return t('select_category') || 'Select category'
    return language === 'fi' ? cat.name_fi : (cat.name_en || cat.name_fi)
  }

  const getSupplierName = (id: string | null) => {
    if (!id) return t('select_supplier') || 'Select supplier'
    const sup = suppliers.find(s => s.id === id)
    return sup?.name || t('select_supplier') || 'Select supplier'
  }

  const getConditionLabel = (value: string) => {
    const condition = CONDITION_GRADES.find(c => c.value === value)
    if (!condition) return t('select_condition') || 'Select condition'
    return language === 'fi' ? condition.labelFi : condition.label
  }

  const SECTION_ICONS: Record<typeof activeSection, LucideIcon> = {
    basic: FileText,
    pricing: Coins,
    inventory: Package,
    details: ClipboardList }

  const renderSectionTab = (key: typeof activeSection, label: string) => {
    const TabIcon = SECTION_ICONS[key]
    return (
    <TouchableOpacity
      key={key}
      style={[styles.sectionTab, activeSection === key && styles.sectionTabActive]}
      onPress={() => setActiveSection(key)}
    >
      <TabIcon size={16} color={activeSection === key ? '#fff' : theme.colors.text.secondary} />
      <Text style={[styles.sectionTabText, activeSection === key && styles.sectionTabTextActive]}>
        {label}
      </Text>
    </TouchableOpacity>
    )
  }

  const renderBasicSection = () => (
    <View style={styles.section}>
      {/* Image with AI button */}
      {images.length > 0 && (
        <View style={styles.imageContainer}>
          <Image source={{ uri: images[0].image_url }} style={styles.productImage} />
          <TouchableOpacity
            style={[styles.aiButton, suggesting && styles.buttonDisabled]}
            onPress={getAISuggestion}
            disabled={suggesting}
          >
            {suggesting ? (
              <Spinner size="small" />
            ) : (
              <Text style={styles.aiButtonText}>{t('ai_suggest') || 'AI Suggest'}</Text>
            )}
          </TouchableOpacity>
        </View>
      )}

      {/* Titles */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('title_fi') || 'Title (Finnish)'} *</Text>
        <TextInput
          style={styles.input}
          value={data.title_fi}
          onChangeText={v => updateField('title_fi', v)}
          placeholder="Tuotteen nimi"
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('title_en') || 'Title (English)'}</Text>
        <TextInput
          style={styles.input}
          value={data.title_en}
          onChangeText={v => updateField('title_en', v)}
          placeholder="Product name"
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      {/* Descriptions */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('description_fi') || 'Description (Finnish)'}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={data.description_fi}
          onChangeText={v => updateField('description_fi', v)}
          placeholder="Tuotteen kuvaus"
          placeholderTextColor={theme.colors.text.secondary}
          multiline
          numberOfLines={3}
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('description_en') || 'Description (English)'}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={data.description_en}
          onChangeText={v => updateField('description_en', v)}
          placeholder="Product description"
          placeholderTextColor={theme.colors.text.secondary}
          multiline
          numberOfLines={3}
        />
      </View>

      {/* Category */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('category') || 'Category'}</Text>
        <TouchableOpacity style={styles.picker} onPress={() => setShowCategoryPicker(true)}>
          <Text style={[styles.pickerText, !data.category_id && styles.pickerPlaceholder]}>
            {getCategoryName(data.category_id)}
          </Text>
          <ChevronRight size={16} color={theme.colors.text.secondary} />
        </TouchableOpacity>
      </View>

      {/* Ownership Type */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('ownership_type') || 'Ownership'}</Text>
        <View style={styles.ownershipButtons}>
          {(['store_owned', 'consignment'] as const).map(type => (
            <TouchableOpacity
              key={type}
              style={[styles.ownershipButton, data.ownership_type === type && styles.ownershipButtonActive]}
              onPress={() => updateField('ownership_type', type)}
            >
              <Text style={[styles.ownershipButtonText, data.ownership_type === type && styles.ownershipButtonTextActive]}>
                {type === 'store_owned' ? (t('store_owned') || 'Store Owned') : (t('consignment') || 'Consignment')}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  )

  const renderPricingSection = () => (
    <View style={styles.section}>
      {/* Cost Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('cost_price') || 'Cost Price'}</Text>
        <View style={styles.inputWithPrefix}>
          <Text style={styles.inputPrefix}>€</Text>
          <TextInput
            style={[styles.input, styles.inputWithPrefixInput]}
            value={data.cost_price}
            onChangeText={v => updateField('cost_price', v)}
            placeholder="0.00"
            placeholderTextColor={theme.colors.text.secondary}
            keyboardType="decimal-pad"
          />
        </View>
        <Text style={styles.hint}>{t('cost_price_hint') || 'What the store paid for the item'}</Text>
      </View>

      {/* Selling Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('selling_price') || 'Selling Price'} *</Text>
        <View style={styles.inputWithPrefix}>
          <Text style={styles.inputPrefix}>€</Text>
          <TextInput
            style={[styles.input, styles.inputWithPrefixInput]}
            value={data.price}
            onChangeText={v => updateField('price', v)}
            placeholder="0.00"
            placeholderTextColor={theme.colors.text.secondary}
            keyboardType="decimal-pad"
          />
        </View>
      </View>

      {/* Margin Display */}
      {calculateMargin() && (
        <View style={styles.marginDisplay}>
          <Text style={styles.marginLabel}>{t('current_margin') || 'Current Margin'}:</Text>
          <Text style={[styles.marginValue, parseFloat(calculateMargin()!) < 20 && styles.marginWarning]}>
            {calculateMargin()}%
          </Text>
        </View>
      )}

      {/* Target Margin */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('target_margin') || 'Target Margin %'}</Text>
        <View style={styles.inputWithSuffix}>
          <TextInput
            style={[styles.input, styles.inputWithSuffixInput]}
            value={data.target_margin_percent}
            onChangeText={v => updateField('target_margin_percent', v)}
            placeholder="50"
            placeholderTextColor={theme.colors.text.secondary}
            keyboardType="decimal-pad"
          />
          <Text style={styles.inputSuffix}>%</Text>
        </View>
        {calculateSuggestedPrice() && (
          <TouchableOpacity
            style={styles.suggestedPrice}
            onPress={() => updateField('price', calculateSuggestedPrice()!)}
          >
            <Text style={styles.suggestedPriceText}>
              {t('suggested_price') || 'Suggested'}: €{calculateSuggestedPrice()} - {t('tap_to_apply') || 'Tap to apply'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Minimum Price */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('minimum_price') || 'Minimum Price (Floor)'}</Text>
        <View style={styles.inputWithPrefix}>
          <Text style={styles.inputPrefix}>€</Text>
          <TextInput
            style={[styles.input, styles.inputWithPrefixInput]}
            value={data.minimum_price}
            onChangeText={v => updateField('minimum_price', v)}
            placeholder="0.00"
            placeholderTextColor={theme.colors.text.secondary}
            keyboardType="decimal-pad"
          />
        </View>
        <Text style={styles.hint}>{t('minimum_price_hint') || 'Will not auto-discount below this'}</Text>
      </View>
    </View>
  )

  const renderInventorySection = () => (
    <View style={styles.section}>
      {/* SKU */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('sku') || 'SKU (Internal Code)'}</Text>
        <TextInput
          style={styles.input}
          value={data.sku}
          onChangeText={v => updateField('sku', v)}
          placeholder="ABC-001"
          placeholderTextColor={theme.colors.text.secondary}
          autoCapitalize="characters"
        />
      </View>

      {/* Barcode */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('barcode') || 'Barcode'}</Text>
        <TextInput
          style={styles.input}
          value={data.barcode}
          onChangeText={v => updateField('barcode', v)}
          placeholder={t('auto_generated') || 'Auto-generated if empty'}
          placeholderTextColor={theme.colors.text.secondary}
        />
      </View>

      {/* Stock Quantity */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('stock_quantity') || 'Stock Quantity'}</Text>
        <TextInput
          style={styles.input}
          value={data.stock_quantity}
          onChangeText={v => updateField('stock_quantity', v)}
          placeholder="1"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="number-pad"
        />
      </View>

      {/* Low Stock Threshold */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('low_stock_threshold') || 'Low Stock Alert Threshold'}</Text>
        <TextInput
          style={styles.input}
          value={data.low_stock_threshold}
          onChangeText={v => updateField('low_stock_threshold', v)}
          placeholder="0"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="number-pad"
        />
        <Text style={styles.hint}>{t('low_stock_hint') || 'Alert when stock falls to or below this'}</Text>
      </View>

      {/* Supplier */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('supplier') || 'Supplier'}</Text>
        <TouchableOpacity style={styles.picker} onPress={() => setShowSupplierPicker(true)}>
          <Text style={[styles.pickerText, !data.supplier_id && styles.pickerPlaceholder]}>
            {getSupplierName(data.supplier_id)}
          </Text>
          <ChevronRight size={16} color={theme.colors.text.secondary} />
        </TouchableOpacity>
      </View>

      {/* Supplier Product Code */}
      {data.supplier_id && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>{t('supplier_code') || "Supplier's Product Code"}</Text>
          <TextInput
            style={styles.input}
            value={data.supplier_product_code}
            onChangeText={v => updateField('supplier_product_code', v)}
            placeholder="Supplier SKU"
            placeholderTextColor={theme.colors.text.secondary}
          />
        </View>
      )}

      {/* Reorder Quantity */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('reorder_quantity') || 'Reorder Quantity'}</Text>
        <TextInput
          style={styles.input}
          value={data.reorder_quantity}
          onChangeText={v => updateField('reorder_quantity', v)}
          placeholder="10"
          placeholderTextColor={theme.colors.text.secondary}
          keyboardType="number-pad"
        />
        <Text style={styles.hint}>{t('reorder_hint') || 'How many to order when restocking'}</Text>
      </View>
    </View>
  )

  const renderDetailsSection = () => (
    <View style={styles.section}>
      {/* Condition Grade */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('condition') || 'Condition'}</Text>
        <TouchableOpacity style={styles.picker} onPress={() => setShowConditionPicker(true)}>
          <Text style={[styles.pickerText, !data.condition_grade && styles.pickerPlaceholder]}>
            {getConditionLabel(data.condition_grade)}
          </Text>
          <ChevronRight size={16} color={theme.colors.text.secondary} />
        </TouchableOpacity>
      </View>

      {/* Condition Notes */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>{t('condition_notes') || 'Condition Notes'}</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={data.condition_notes}
          onChangeText={v => updateField('condition_notes', v)}
          placeholder={t('condition_notes_placeholder') || 'Describe any wear, damage, or notable features...'}
          placeholderTextColor={theme.colors.text.secondary}
          multiline
          numberOfLines={4}
        />
      </View>
    </View>
  )

  // Picker modals
  const renderCategoryPicker = () => (
    <Modal visible={showCategoryPicker} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_category') || 'Select Category'}</Text>
            <TouchableOpacity onPress={() => setShowCategoryPicker(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={categories}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.pickerItem, data.category_id === item.id && styles.pickerItemSelected]}
                onPress={() => {
                  updateField('category_id', item.id)
                  setShowCategoryPicker(false)
                }}
              >
                {item.icon && <Text style={styles.pickerItemIcon}>{item.icon}</Text>}
                <Text style={styles.pickerItemText}>
                  {language === 'fi' ? item.name_fi : (item.name_en || item.name_fi)}
                </Text>
                {data.category_id === item.id && <Check size={18} color={merchantColors.primary} />}
              </TouchableOpacity>
            )}
            ListHeaderComponent={
              <TouchableOpacity
                style={styles.pickerItem}
                onPress={() => {
                  updateField('category_id', null)
                  setShowCategoryPicker(false)
                }}
              >
                <Text style={styles.pickerItemText}>{t('no_category') || 'No category'}</Text>
              </TouchableOpacity>
            }
          />
        </View>
      </View>
    </Modal>
  )

  const renderSupplierPicker = () => (
    <Modal visible={showSupplierPicker} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_supplier') || 'Select Supplier'}</Text>
            <TouchableOpacity onPress={() => setShowSupplierPicker(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={suppliers}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.pickerItem, data.supplier_id === item.id && styles.pickerItemSelected]}
                onPress={() => {
                  updateField('supplier_id', item.id)
                  setShowSupplierPicker(false)
                }}
              >
                <Text style={styles.pickerItemText}>{item.name}</Text>
                {data.supplier_id === item.id && <Check size={18} color={merchantColors.primary} />}
              </TouchableOpacity>
            )}
            ListHeaderComponent={
              <TouchableOpacity
                style={styles.pickerItem}
                onPress={() => {
                  updateField('supplier_id', null)
                  setShowSupplierPicker(false)
                }}
              >
                <Text style={styles.pickerItemText}>{t('no_supplier') || 'No supplier'}</Text>
              </TouchableOpacity>
            }
            ListEmptyComponent={
              <View style={styles.emptyPicker}>
                <Text style={styles.emptyPickerText}>{t('no_suppliers') || 'No suppliers added yet'}</Text>
                <TouchableOpacity
                  style={styles.emptyPickerButton}
                  onPress={() => {
                    setShowSupplierPicker(false)
                    navigation.navigate('SupplierManagement', { storeId })
                  }}
                >
                  <Text style={styles.emptyPickerButtonText}>{t('add_supplier') || 'Add Supplier'}</Text>
                </TouchableOpacity>
              </View>
            }
          />
        </View>
      </View>
    </Modal>
  )

  const renderConditionPicker = () => (
    <Modal visible={showConditionPicker} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('select_condition') || 'Select Condition'}</Text>
            <TouchableOpacity onPress={() => setShowConditionPicker(false)} style={styles.modalCloseButton}>
              <X size={24} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={CONDITION_GRADES}
            keyExtractor={item => item.value}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.pickerItem, data.condition_grade === item.value && styles.pickerItemSelected]}
                onPress={() => {
                  updateField('condition_grade', item.value)
                  setShowConditionPicker(false)
                }}
              >
                <Text style={styles.pickerItemText}>
                  {language === 'fi' ? item.labelFi : item.label}
                </Text>
                {data.condition_grade === item.value && <Check size={18} color={merchantColors.primary} />}
              </TouchableOpacity>
            )}
          />
        </View>
      </View>
    </Modal>
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButtonContainer}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('cancel') || 'Cancel'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {isNew ? (t('new_product') || 'New Product') : (t('edit_product') || 'Edit Product')}
        </Text>
        <View style={{ width: 60 }} />
      </View>

      {/* Section Tabs */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsContainer}
        contentContainerStyle={styles.tabsContent}
      >
        {renderSectionTab('basic', t('basic_info') || 'Basic')}
        {renderSectionTab('pricing', t('pricing') || 'Pricing')}
        {renderSectionTab('inventory', t('inventory') || 'Inventory')}
        {renderSectionTab('details', t('details') || 'Details')}
      </ScrollView>

      {/* Content */}
      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {activeSection === 'basic' && renderBasicSection()}
        {activeSection === 'pricing' && renderPricingSection()}
        {activeSection === 'inventory' && renderInventorySection()}
        {activeSection === 'details' && renderDetailsSection()}

        {/* Action Buttons */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.publishButton, saving && styles.buttonDisabled]}
            onPress={() => saveProduct(true)}
            disabled={saving}
          >
            {saving ? (
              <Spinner color="#fff" />
            ) : (
              <Text style={styles.publishButtonText}>{t('publish') || 'Publish'}</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.draftButton, saving && styles.buttonDisabled]}
            onPress={() => saveProduct(false)}
            disabled={saving}
          >
            <Text style={styles.draftButtonText}>{t('save_draft') || 'Save Draft'}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Pickers */}
      {renderCategoryPicker()}
      {renderSupplierPicker()}
      {renderConditionPicker()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  tabsContainer: {
    backgroundColor: theme.colors.background.card,
    maxHeight: 60,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  tabsContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8 },
  sectionTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.background.secondary,
    marginRight: 8 },
  sectionTabActive: {
    backgroundColor: merchantColors.primary },
  sectionTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  sectionTabTextActive: {
    color: '#fff' },
  content: {
    flex: 1,
    padding: theme.spacing.md },
  section: {
    marginBottom: theme.spacing.md },
  imageContainer: {
    position: 'relative',
    marginBottom: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden' },
  productImage: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: theme.colors.background.secondary },
  aiButton: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    backgroundColor: '#9C27B0',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    ...theme.shadows.medium },
  aiButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  inputGroup: {
    marginBottom: 20 },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  input: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    fontSize: 16,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    color: theme.colors.text.primary },
  textArea: {
    height: 100,
    textAlignVertical: 'top' },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6 },
  inputWithPrefix: {
    flexDirection: 'row',
    alignItems: 'center' },
  inputPrefix: {
    position: 'absolute',
    left: 14,
    fontSize: 16,
    color: theme.colors.text.secondary,
    zIndex: 1 },
  inputWithPrefixInput: {
    flex: 1,
    paddingLeft: 30 },
  inputWithSuffix: {
    flexDirection: 'row',
    alignItems: 'center' },
  inputWithSuffixInput: {
    flex: 1,
    paddingRight: 40 },
  inputSuffix: {
    position: 'absolute',
    right: 14,
    fontSize: 16,
    color: theme.colors.text.secondary },
  picker: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 14,
    borderWidth: 2,
    borderColor: theme.colors.ui.border },
  pickerText: {
    fontSize: 16,
    color: theme.colors.text.primary },
  pickerPlaceholder: {
    color: theme.colors.text.secondary },
  ownershipButtons: {
    flexDirection: 'row',
    gap: 12 },
  ownershipButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.card,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    alignItems: 'center' },
  ownershipButtonActive: {
    borderColor: merchantColors.primary,
    backgroundColor: `${merchantColors.primary}20` },
  ownershipButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  ownershipButtonTextActive: {
    color: merchantColors.primary },
  marginDisplay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    padding: 12,
    borderRadius: theme.borderRadius.md,
    marginBottom: 20 },
  marginLabel: {
    fontSize: 14,
    color: '#388E3C',
    fontWeight: '500' },
  marginValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#388E3C' },
  marginWarning: {
    color: '#F57C00' },
  suggestedPrice: {
    marginTop: 8,
    padding: 10,
    backgroundColor: '#E3F2FD',
    borderRadius: theme.borderRadius.sm },
  suggestedPriceText: {
    fontSize: 13,
    color: '#1976D2',
    textAlign: 'center' },
  actions: {
    marginTop: theme.spacing.lg,
    gap: 12 },
  publishButton: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center',
    ...theme.shadows.medium },
  publishButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600' },
  draftButton: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 16,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: merchantColors.primary },
  draftButtonText: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '600' },
  buttonDisabled: {
    opacity: 0.6 },
  bottomSpacer: {
    height: 120 },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '70%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  modalCloseButton: {
    padding: 4 },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  pickerItemSelected: {
    backgroundColor: `${merchantColors.primary}10` },
  pickerItemIcon: {
    fontSize: 20,
    marginRight: 12 },
  pickerItemText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text.primary },
  emptyPicker: {
    padding: theme.spacing.xl,
    alignItems: 'center' },
  emptyPickerText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md },
  emptyPickerButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md },
  emptyPickerButtonText: {
    color: '#fff',
    fontWeight: '600' } })
